/*
 * Simulated points for what a plant does not measure yet: an electricity
 * meter, heat output, coefficient of performance, monthly figures, the
 * §14a EnWG state over EEBUS, PV on the roof. Keys start with "sim/" and are
 * served next to the real ones (see withSimulatedPoints). Patrick,
 * 15.10.2026: "simuliere den Stromzaehler, substituiere mit Demowerten da
 * und dort", for showing the TP8 with a Siemens RVS that has only heat meters.
 *
 * The values follow the time of day so a trend over 24 h looks like a heat
 * pump: more power in the cold morning and evening, PV around noon.
 */

import type {
  HistoryAggregation, IPointBindingSource, PointHistorySample, PointValue, WriteAcknowledgement,
} from './binding';

export const SIM_PREFIX = 'sim/';
export const isSimKey = (key: string): boolean => key.toLowerCase().startsWith(SIM_PREFIX);

const HOUR = 3_600_000;

/** Wert eines Simulationspunkts zum Zeitpunkt t (ms). Deterministisch bis auf
 *  ein wenig Rauschen, damit Verlauf und Jetzt-Wert zusammenpassen. */
function at(name: string, t: number, noise = true): unknown {
  const d = new Date(t);
  const h = d.getHours() + d.getMinutes() / 60;
  const jitter = (a: number) => (noise ? (Math.random() - 0.5) * a : 0);
  // Heizlast: morgens und abends hoeher, mittags niedriger.
  const load = 0.62 + 0.25 * Math.cos(((h - 6.5) / 24) * 2 * Math.PI) + 0.12 * Math.cos(((h - 19) / 12) * 2 * Math.PI);
  const el = Math.max(0.35, 1.55 * load + jitter(0.06));
  const cop = 3.95 + 0.35 * Math.sin(((h - 9) / 24) * 2 * Math.PI) + jitter(0.05);
  const pv = Math.max(0, 4.2 * Math.sin(((h - 7) / 12) * Math.PI)) * (h > 7 && h < 19 ? 1 : 0);
  switch (name) {
    case 'el-power': return round(el, 2);
    case 'cop': return round(cop, 2);
    case 'heat-power': return round(el * cop, 2);
    case 'jaz': return 3.92;
    case 'months': return monthValues(d);
    case 'year-elec': return Math.round(2350 + yearFraction(d) * 600);
    case 'year-heat': return Math.round(9180 + yearFraction(d) * 2400);
    case 'eebus-state': return 'bereit';
    case 'eebus-limit': return 4.2;
    case 'pv-power': return round(pv + (pv > 0 ? jitter(0.1) : 0), 2);
    case 'pv-surplus': return pv - el > 0.3 ? 1 : 0;
    case 'house-power': return round(0.45 + jitter(0.08) + (h > 17 && h < 22 ? 0.6 : 0), 2);
    case 'grid-power': return round(el + 0.45 - pv, 2);
    default: return null;
  }
}

function round(v: number, d: number): number {
  const f = 10 ** d;
  return Math.round(v * f) / f;
}

function yearFraction(d: Date): number {
  const start = new Date(d.getFullYear(), 0, 1).getTime();
  return (d.getTime() - start) / (365 * 24 * HOUR);
}

/** Arbeitszahl je Monat, Jaenner bis Dezember; kommende Monate leer. */
function monthValues(d: Date): (number | null)[] {
  const typical = [3.4, 3.5, 3.8, 4.2, 4.6, 4.9, 5.0, 4.9, 4.5, 4.1, 3.7, 3.5];
  const cur = d.getMonth();
  return typical.map((v, i) => (i < cur ? v : i === cur ? round(v - 0.1, 1) : null));
}

export class SimulatedPointSource implements IPointBindingSource {
  async *subscribe(pointId: string, signal: AbortSignal): AsyncIterable<PointValue> {
    const name = pointId.slice(SIM_PREFIX.length).toLowerCase();
    while (!signal.aborted) {
      const now = Date.now();
      yield { value: at(name, now), quality: 'good', timestamp: new Date(now).toISOString(), receivedAt: now };
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, 5000);
        signal.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
      });
    }
  }

  // eslint-disable-next-line require-yield
  async *write(_pointId: string, _value: unknown, _signal: AbortSignal): AsyncIterable<WriteAcknowledgement> {
    yield { correlationId: 'sim', state: 'rejected', echoValue: null, at: new Date().toISOString(), error: 'Simulierter Wert' };
  }

  async getHistory(
    pointId: string, since: Date, until: Date, _aggregation: HistoryAggregation, _signal: AbortSignal,
  ): Promise<PointHistorySample[]> {
    const name = pointId.slice(SIM_PREFIX.length).toLowerCase();
    const out: PointHistorySample[] = [];
    const step = Math.max(5 * 60_000, (until.getTime() - since.getTime()) / 400);
    for (let t = since.getTime(); t <= until.getTime(); t += step) {
      const v = at(name, t, true);
      out.push({ timestamp: new Date(t).toISOString(), value: typeof v === 'number' ? v : null, quality: 'good' });
    }
    return out;
  }
}

/** Reicht "sim/..." an die Simulation, alles andere an die echte Quelle. */
export function withSimulatedPoints(real: IPointBindingSource): IPointBindingSource {
  const sim = new SimulatedPointSource();
  return {
    subscribe: (id, signal) => (isSimKey(id) ? sim.subscribe(id, signal) : real.subscribe(id, signal)),
    write: (id, value, signal) => (isSimKey(id) ? sim.write(id, value, signal) : real.write(id, value, signal)),
    getHistory: (id, since, until, agg, signal, widthPx) =>
      (isSimKey(id) ? sim.getHistory(id, since, until, agg, signal) : real.getHistory(id, since, until, agg, signal, widthPx)),
  };
}
